import { useEffect, useRef, useState } from "react";
import { Play, Pause, ChevronLeft, ChevronRight, Volume2, VolumeX, X, ArrowRight } from "lucide-react";
import { CHAPTERS, SLIDES } from "@/components/tourSlides";
import { googleLogin } from "@/pages/Landing";

const DURATION = 7000;

const Chapters = ({ i, go }) => (
  <div className="flex flex-wrap gap-2 mt-8" data-testid="tour-chapters">
    {CHAPTERS.map((name, c) => {
      const first = SLIDES.findIndex((s) => s.c === c);
      const active = SLIDES[i].c === c;
      return (
        <button key={name} onClick={() => go(first)} data-testid={`tour-chapter-${c}`}
          className={`rounded-full px-4 py-2 text-sm border transition-colors ${active ? "bg-[#0F172A] text-white border-[#0F172A]" : "border-slate-300 hover:border-sky-500 hover:text-sky-700"}`}>
          {name}
        </button>
      );
    })}
  </div>
);

const Spots = ({ s, open, setOpen }) => s.spots.map((p, k) => (
  <div key={p.t} className="absolute" style={{ left: `${p.x}%`, top: `${p.y}%` }}>
    <button onClick={(e) => { e.stopPropagation(); setOpen(open === k ? null : k); }} data-testid={`tour-spot-${k}`} aria-label={p.t}
      className="tour-spot -translate-x-1/2 -translate-y-1/2 h-7 w-7 rounded-full bg-sky-500 text-white text-xs font-bold grid place-items-center ring-4 ring-sky-300/50 shadow-lg">
      {k + 1}
    </button>
    {open === k && (
      <div data-testid={`tour-spot-card-${k}`} onClick={(e) => e.stopPropagation()}
        className={`absolute z-10 mt-3 w-64 rounded-xl bg-white p-4 shadow-2xl border border-slate-200 text-left ${p.x > 60 ? "right-0" : "-left-3"} ${p.y > 60 ? "bottom-8" : "top-2"}`}>
        <div className="flex items-start justify-between gap-2"><p className="font-semibold text-sm text-slate-900">{p.t}</p>
          <button onClick={() => setOpen(null)} aria-label="Chiudi" data-testid="tour-spot-close"><X className="h-4 w-4 text-slate-400" /></button></div>
        <p className="text-xs text-slate-600 mt-1 leading-relaxed">{p.d}</p>
      </div>
    )}
  </div>
));

const Screen = ({ i, s, dur, playing, open, setOpen }) => (
  <div className="relative overflow-hidden rounded-xl aspect-[16/10] bg-slate-100" onClick={() => setOpen(null)}>
    {SLIDES.map((x, k) => (
      <img key={x.img} src={`/tour/${x.img}.jpg?v=3`} alt={x.title} data-testid={`tour-slide-${k}`} loading={k < 2 ? "eager" : "lazy"}
        style={k === i ? { transformOrigin: `${x.spots[0].x}% ${x.spots[0].y}%`, animationDuration: `${dur}ms`, animationPlayState: playing ? "running" : "paused" } : undefined}
        className={`absolute inset-0 h-full w-full object-cover object-top transition-opacity duration-700 ${k === i ? "opacity-100 tour-zoom" : "opacity-0"}`} />
    ))}
    <div key={`spots-${i}`} className="absolute inset-0 tour-zoom pointer-events-none [&>*]:pointer-events-auto"
      style={{ transformOrigin: `${s.spots[0].x}% ${s.spots[0].y}%`, animationDuration: `${dur}ms`, animationPlayState: playing ? "running" : "paused" }}>
      <Spots s={s} open={open} setOpen={setOpen} />
    </div>
  </div>
);

const useNarration = (i, voice, playing, go, setVoice) => {
  const audio = useRef(null);
  const [dur, setDur] = useState(DURATION);
  useEffect(() => {
    if (!playing || voice) return undefined;
    const t = setTimeout(() => go(i + 1), DURATION);
    return () => clearTimeout(t);
  }, [i, playing, voice]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const a = audio.current;
    if (!a) return undefined;
    if (!voice) { a.pause(); setDur(DURATION); return undefined; }
    a.src = `/tour/voice-${String(i + 1).padStart(2, "0")}.mp3?v=4`;
    a.onloadedmetadata = () => setDur(Math.round((a.duration + 0.8) * 1000));
    let t;
    a.onended = () => { t = setTimeout(() => go(i + 1), 800); };
    if (playing) a.play().catch(() => setVoice(false));
    return () => { clearTimeout(t); a.onended = null; a.pause(); };
  }, [i, voice, playing]); // eslint-disable-line react-hooks/exhaustive-deps
  return { audio, dur };
};

export const ProductTour = () => {
  const [i, setI] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [voice, setVoice] = useState(false);
  const [open, setOpen] = useState(null);
  const go = (n) => { setOpen(null); setI((n + SLIDES.length) % SLIDES.length); };
  const { audio, dur } = useNarration(i, voice, playing, go, setVoice);
  const openSpot = (k) => { setOpen(k); if (k !== null) setPlaying(false); };
  const s = SLIDES[i];
  return (
    <section id="tour" className="px-6 lg:px-16 py-20" data-testid="product-tour">
      <p className="text-xs uppercase tracking-[0.2em] text-sky-700 font-semibold">Guarda come funziona</p>
      <h2 className="font-display text-3xl lg:text-4xl font-bold mt-2 max-w-2xl">Un giro completo dentro Kreditofacile</h2>
      <p className="text-slate-500 mt-2 text-sm">Scegli un capitolo o tocca i punti numerati sulle schermate per scoprire i dettagli.</p>
      <Chapters i={i} go={go} />
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-8 items-start">
        <div className="min-w-0 max-w-full rounded-2xl bg-[#0F172A] p-2 shadow-2xl">
          <div className="flex min-w-0 items-center gap-1.5 px-3 py-2">
            <span className="h-2.5 w-2.5 rounded-full bg-rose-400" /><span className="h-2.5 w-2.5 rounded-full bg-amber-400" /><span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
            <span className="ml-3 min-w-0 flex-1 text-[11px] text-slate-400 font-mono truncate">kreditofacile · {CHAPTERS[s.c]} · {s.title}</span>
          </div>
          <Screen i={i} s={s} dur={dur} playing={playing} open={open} setOpen={openSpot} />
          <div className="flex gap-1 px-2 pt-2.5 pb-1">
            {SLIDES.map((x, k) => (
              <button key={x.img} onClick={() => go(k)} data-testid={`tour-dot-${k}`} className="h-1 flex-1 rounded-full bg-white/15 overflow-hidden" aria-label={x.title}>
                <span className={`block h-full bg-sky-400 ${k < i ? "w-full" : k === i && playing ? "tour-progress" : k === i ? "w-1/2" : "w-0"}`} key={`${k}-${i}-${playing}-${dur}`} style={k === i && playing ? { animationDuration: `${dur}ms` } : undefined} />
              </button>
            ))}
          </div>
        </div>
        <div className="min-w-0 space-y-5">
          <p className="font-mono text-sm text-slate-500" data-testid="tour-counter">{String(i + 1).padStart(2, "0")} / {String(SLIDES.length).padStart(2, "0")} · {CHAPTERS[s.c]}</p>
          <h3 className="font-display text-2xl font-bold" data-testid="tour-title">{s.title}</h3>
          <p className="text-slate-600 leading-relaxed" data-testid="tour-text">{s.text}</p>
          <ul className="space-y-1.5" data-testid="tour-spot-list">
            {s.spots.map((p, k) => (
              <li key={p.t}><button onClick={() => openSpot(k)} data-testid={`tour-spot-link-${k}`} className="text-sm text-sky-700 hover:underline text-left"><span className="font-mono text-slate-500 mr-2">{k + 1}</span>{p.t}</button></li>
            ))}
          </ul>
          <audio ref={audio} preload="none" data-testid="tour-audio" />
          <div className="flex flex-wrap items-center gap-2 pt-2">
            <button onClick={() => go(i - 1)} data-testid="tour-prev" className="h-11 w-11 grid place-items-center rounded-full border border-slate-300 hover:border-sky-500 transition-colors" aria-label="Precedente"><ChevronLeft className="h-5 w-5" /></button>
            <button onClick={() => { setOpen(null); setPlaying((p) => !p); }} data-testid="tour-play" className="h-11 px-5 flex items-center gap-2 rounded-full bg-[#0F172A] text-white hover:bg-sky-600 transition-colors">
              {playing ? <><Pause className="h-4 w-4" />Pausa</> : <><Play className="h-4 w-4" />Riproduci</>}
            </button>
            <button onClick={() => { setVoice((v) => !v); setPlaying(true); setOpen(null); }} data-testid="tour-voice" className={`h-11 px-4 flex items-center gap-2 rounded-full border transition-colors ${voice ? "border-sky-500 bg-sky-50 text-sky-700" : "border-slate-300 hover:border-sky-500"}`} aria-label="Voce narrante">
              {voice ? <><Volume2 className="h-4 w-4" />Voce attiva</> : <><VolumeX className="h-4 w-4" />Attiva voce</>}
            </button>
            <button onClick={() => go(i + 1)} data-testid="tour-next" className="h-11 w-11 grid place-items-center rounded-full border border-slate-300 hover:border-sky-500 transition-colors" aria-label="Successiva"><ChevronRight className="h-5 w-5" /></button>
          </div>
          {s.cta && (
            <button onClick={googleLogin} data-testid="tour-cta" className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-medium bg-sky-700 text-white hover:bg-sky-800 transition-colors">
              Prova gratis 30 giorni <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </section>
  );
};
